// src/components/charts/composed/MultiTokenChart.tsx
/**
 * MultiTokenChart Component
 * 
 * A powerful multi-series chart designed for token comparison with:
 * - Multiple token/series support with legend
 * - Index-to-100 normalization for fair comparison
 * - Log scale support
 * - Data zoom (slider + mouse wheel)
 * - Toolbox (save image, CSV export, reset)
 * - Multiple chart types (line, bar, stacked, 100-stacked)
 * - Consistent dark theme styling
 * 
 * This component is designed for use cases where users compare multiple
 * tokens/assets across the same metric, like in TokenScreener CompareTab.
 * 
 * @example
 * <MultiTokenChart
 *   dates={['2024-01-01', '2024-01-02', ...]}
 *   series={[
 *     { name: 'BTC', values: [...], color: '#f7931a' },
 *     { name: 'ETH', values: [...], color: '#627eea' },
 *   ]}
 *   indexTo100
 *   showDataZoom
 *   showToolbox
 * />
 */

import React, { useMemo, useCallback, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption, EChartsType } from 'echarts';

import {
  CHART_COLORS,
  createTooltip,
  createGrid,
  createLegend,
  createXAxis,
  createYAxis,
  smartTooltipPosition,
} from '@/utils/charts/theme';
import { hexToRgba } from '@/utils/colors';
import {
  formatCurrencyShort,
  formatPercent,
  formatRatio,
  formatDateLabel,
} from '@/utils/charts/formatters';

// ============================================================================
// TYPES
// ============================================================================

export type MultiTokenYAxisFormat = 'currency' | 'percent' | 'ratio' | 'number';
export type MultiTokenChartType = 'line' | 'bar' | 'stacked' | '100-stacked';

export interface MultiTokenSeriesConfig {
  name: string;
  values: (number | null)[];
  color?: string | undefined;
}

export interface MultiTokenChartProps {
  /** Date labels (ISO strings or YYYYMMDD integers) */
  dates: (string | number)[];
  /** Token/series data */
  series: MultiTokenSeriesConfig[];
  /** Chart height in pixels or CSS value */
  height?: number | string;
  /** Chart type */
  chartType?: MultiTokenChartType;
  /** Index all series to 100 at start for fair comparison */
  indexTo100?: boolean;
  /** Use logarithmic Y-axis scale */
  logScale?: boolean;
  /** Y-axis format type */
  yAxisFormat?: MultiTokenYAxisFormat;
  /** Custom Y-axis formatter */
  yAxisFormatter?: (value: number) => string;
  /** Y-axis label */
  yAxisName?: string;
  /** Show data zoom slider */
  showDataZoom?: boolean;
  /** Show toolbox (save, reset, CSV) */
  showToolbox?: boolean;
  /** Show legend */
  showLegend?: boolean;
  /** Enable CSV export (requires onExportCsv or uses default) */
  enableCsvExport?: boolean;
  /** Custom CSV export handler */
  onExportCsv?: () => void;
  /** Metric ID for export naming */
  metricId?: string;
  /** Additional className */
  className?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function getYAxisFormatter(format: MultiTokenYAxisFormat): (v: number) => string {
  switch (format) {
    case 'currency':
      return formatCurrencyShort;
    case 'percent':
      return (v: number) => formatPercent(v, { decimals: 1 });
    case 'ratio':
      return (v: number) => formatRatio(v, 2);
    default:
      return (v: number) => {
        if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
        if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
        if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
        return v.toFixed(2);
      };
  }
}

function indexSeriesToBase(
  values: (number | null)[],
  baseIndex: number = 100
): number[] {
  const firstValid = values.findIndex(v => v !== null && Number.isFinite(v) && v !== 0);
  if (firstValid === -1) return values.map(() => baseIndex);
  
  const base = values[firstValid] as number;
  return values.map(v => {
    if (v === null || !Number.isFinite(v)) return NaN;
    return (v / base) * baseIndex;
  });
}

// ============================================================================
// COMPONENT
// ============================================================================

const MultiTokenChart: React.FC<MultiTokenChartProps> = ({
  dates,
  series,
  height = 500,
  chartType = 'line',
  indexTo100 = false,
  logScale = false,
  yAxisFormat = 'number',
  yAxisFormatter: customYAxisFormatter,
  yAxisName,
  showDataZoom = true,
  showToolbox = true,
  showLegend = true,
  enableCsvExport = true,
  onExportCsv,
  metricId = 'data',
  className = '',
}) => {
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({});
  const chartRef = React.useRef<EChartsType | null>(null);
  
  // Handle legend selection changes
  const onChartReady = useCallback((chart: EChartsType) => {
    chartRef.current = chart;
    chart.on('legendselectchanged', (params: unknown) => {
      const p = params as { selected: Record<string, boolean> };
      setLegendSelected(p.selected);
    });
  }, []);
  
  // Default CSV export handler
  const handleCsvExport = useCallback(() => {
    if (onExportCsv) {
      onExportCsv();
      return;
    }
    
    try {
      const header = ['date', ...series.map(s => s.name)];
      const rows = dates.map((date, idx) => {
        const dateStr = typeof date === 'number' && date > 10000000
          ? formatDateLabel(date, 'short')
          : String(date);
        const values = series.map(s => {
          const v = s.values[idx];
          return v !== null && Number.isFinite(v) ? String(v) : '';
        });
        return [dateStr, ...values].join(',');
      });
      
      const csv = [header.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${metricId}_export.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
    }
  }, [dates, series, metricId, onExportCsv]);
  
  const option = useMemo(() => {
    // Format dates for display - dates can be numeric YYYYMMDD, ISO strings, or pre-formatted strings
    const dateLabels = dates.map(d => {
      // Handle numeric YYYYMMDD format
      if (typeof d === 'number' && d > 10000000) {
        return formatDateLabel(d, 'short');
      }
      // Handle ISO string dates (contains 'T')
      if (typeof d === 'string' && d.includes('T')) {
        return formatDateLabel(d, 'short');
      }
      // Already formatted string - use as-is
      return String(d);
    });
    
    // Process series data
    const processedSeries = series.map((s, idx) => {
      let values = s.values;
      
      // Apply index-to-100 normalization
      if (indexTo100) {
        values = indexSeriesToBase(values, 100);
      }
      
      return {
        name: s.name,
        values,
        color: s.color || CHART_COLORS.series[idx % CHART_COLORS.series.length],
      };
    });
    
    // Handle 100% stacked normalization
    let normalizedSeries = processedSeries;
    if (chartType === '100-stacked') {
      const n = dates.length;
      const normalized = processedSeries.map(s => ({ ...s, values: [...s.values] }));
      for (let idx = 0; idx < n; idx++) {
        const total = normalized.reduce((sum, s) => {
          const v = s.values[idx];
          return sum + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
        }, 0);
        if (total > 0) {
          normalized.forEach(s => {
            const v = s.values[idx];
            s.values[idx] = typeof v === 'number' ? (v / total) * 100 : 0;
          });
        }
      }
      normalizedSeries = normalized;
    }
    
    const yFormatter = customYAxisFormatter || getYAxisFormatter(
      chartType === '100-stacked' ? 'percent' : yAxisFormat
    );
    
    // Build ECharts series
    // Stacked and 100-stacked should render as bar charts (stacked bars)
    const isStacked = chartType === 'stacked' || chartType === '100-stacked';
    const isBar = chartType === 'bar' || isStacked; // Stacked charts use bars
    const isLine = chartType === 'line';
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const echartsSeries: any[] = normalizedSeries.map(s => {
      // Area opacity for line charts only
      const areaOpacity = 0.3;
      
      const baseSeries = {
        name: s.name,
        type: isBar ? 'bar' : 'line',
        data: s.values,
        smooth: false,
        showSymbol: false,
        itemStyle: { color: s.color },
        // Add emphasis/blur for better interactivity
        emphasis: {
          focus: 'series' as const,
          ...(isLine ? { lineStyle: { width: 3 } } : {}),
        },
        blur: {
          itemStyle: { opacity: 0.2 },
          ...(isLine ? { lineStyle: { opacity: 0.25 } } : {}),
        },
      };
      
      if (isBar) {
        return {
          ...baseSeries,
          ...(isStacked ? { stack: 'total', stackStrategy: 'all' as const } : {}),
        };
      }
      
      // Line series with area fill
      return {
        ...baseSeries,
        lineStyle: { width: 2, color: s.color },
        areaStyle: { 
          opacity: areaOpacity,
          color: s.color ? hexToRgba(s.color, areaOpacity) : undefined,
        },
      };
    });
    
    const metricKey = String(metricId || '').toLowerCase();
    const isFundingMetric = metricKey.includes('futures_fr') || metricKey.includes('funding');

    // Tooltip formatter
    const tooltipFormatter = (params: unknown) => {
      const items = params as Array<{
        axisValue: string;
        seriesName: string;
        value: number;
        marker: string;
        color: string;
      }>;
      if (!items || items.length === 0) return '';
      
      const dateStr = items[0]?.axisValue || '';
      let html = `<div style="font-size:11px;font-weight:500;margin-bottom:6px;color:#9ca3af;">${dateStr}</div>`;
      
      // Sort by value descending
      const sorted = [...items].sort((a, b) => (b.value || 0) - (a.value || 0));
      
      sorted.forEach(item => {
        const val = Number(item.value);
        if (!Number.isFinite(val)) return;
        
        const formatted = yFormatter(val);
        const valueColor = isFundingMetric
          ? (val > 0 ? CHART_COLORS.semantic.positive : val < 0 ? CHART_COLORS.semantic.negative : CHART_COLORS.text.secondary)
          : CHART_COLORS.text.primary;
        html += `<div style="display:flex;align-items:center;gap:8px;margin:2px 0;">
          <span style="width:10px;height:10px;border-radius:2px;background:${item.color}"></span>
          <span style="opacity:.9">${item.seriesName}</span>
          <span style="margin-left:auto;font-weight:600;color:${valueColor}">${formatted}</span>
        </div>`;
      });
      
      return html;
    };
    
    // Build toolbox features
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toolboxFeature: Record<string, any> = {
      saveAsImage: { show: true, title: 'Save' },
      restore: { show: true, title: 'Reset' },
    };
    
    if (enableCsvExport) {
      toolboxFeature['mySaveCsv'] = {
        show: true,
        title: 'CSV',
        icon: 'path://M5 3h10a2 2 0 0 1 2 2v3h-2V5H5v10h10v-3h2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm6 5h8v2h-8V8zm0 4h8v2h-8v-2z',
        onclick: handleCsvExport,
      };
    }
    
    return {
      backgroundColor: CHART_COLORS.background,
      tooltip: {
        ...createTooltip(),
        trigger: 'axis',
        position: smartTooltipPosition,
        formatter: tooltipFormatter,
        axisPointer: { type: 'cross', crossStyle: { color: '#64748b' } },
      },
      legend: showLegend ? {
        ...createLegend(),
        type: 'scroll',
        selected: Object.keys(legendSelected).length > 0 ? legendSelected : undefined,
      } : { show: false },
      grid: createGrid({
        bottom: showDataZoom ? 88 : 40,
        top: showLegend && series.length > 1 ? 28 : 12,
      }),
      xAxis: {
        ...createXAxis({ data: dateLabels, boundaryGap: isBar }), // isBar already includes stacked
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
        },
      },
      yAxis: createYAxis({
        formatter: yFormatter,
        ...(yAxisName ? { name: yAxisName } : {}),
        ...(logScale ? { type: 'log' } : {}),
        ...(chartType === '100-stacked' ? { min: 0, max: 100 } : {}),
      }),
      ...(showDataZoom ? {
        dataZoom: [
          { type: 'inside', throttle: 50, zoomOnMouseWheel: true, moveOnMouseMove: true },
          { type: 'slider', height: 30, bottom: 24, textStyle: { color: CHART_COLORS.axis.label }, brushSelect: false },
        ],
      } : {}),
      ...(showToolbox ? {
        toolbox: {
          show: true,
          orient: 'horizontal',
          itemSize: 14,
          right: 12,
          top: showLegend && series.length > 1 ? 28 : 6,
          feature: toolboxFeature,
          iconStyle: { borderColor: CHART_COLORS.axis.label },
          emphasis: { iconStyle: { borderColor: CHART_COLORS.text.primary } },
        },
      } : {}),
      series: echartsSeries,
      animationDuration: 400,
      animationEasing: 'quarticOut',
    } as EChartsOption;
  }, [
    dates, series, chartType, indexTo100, logScale, yAxisFormat, customYAxisFormatter,
    yAxisName, showDataZoom, showToolbox, showLegend, enableCsvExport, legendSelected,
    handleCsvExport,
    metricId,
  ]);
  
  return (
    <div className={className}>
      <ReactECharts
        option={option}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        onChartReady={onChartReady}
        notMerge
        lazyUpdate
      />
    </div>
  );
};

export default MultiTokenChart;

